import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button, Input, Label, Textarea } from "@ndd/ui";
import { cn } from "@/lib/utils";
import { todayIsoDate } from "../../../../shared/local-date";

export interface TranscriptFormResult {
  sessionId: string;
  sessionShortId: string;
  clientName: string | null;
  sessionDate: string | null;
}

interface TranscriptFormProps {
  endpoint: string;
  variant: "inline" | "overlay";
  title: string;
  subtitle?: string;
  submitLabel: string;
  helperText?: string;
  requireSessionDate?: boolean;
  initialTranscript?: string;
  onSuccess: (result: TranscriptFormResult) => void;
  onClose: () => void;
}

const FIELD_BASE =
  "h-9 rounded-[10px] bg-sand-50 border-sand-200 text-foreground text-[13px] md:text-[13px] px-3 placeholder:text-sand-400 shadow-none";

const LABEL_BASE =
  "text-[11px] font-semibold uppercase tracking-wider text-sand-500";

export function TranscriptForm({
  endpoint,
  variant,
  title,
  subtitle,
  submitLabel,
  helperText = "Analysis takes about 2 minutes.",
  requireSessionDate = false,
  initialTranscript,
  onSuccess,
  onClose,
}: TranscriptFormProps) {
  const [clientName, setClientName] = useState("");
  const [sessionDate, setSessionDate] = useState(todayIsoDate);
  const [transcript, setTranscript] = useState(initialTranscript ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateInvalid, setDateInvalid] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (requireSessionDate && !sessionDate) {
      setDateInvalid(true);
      return;
    }

    const trimmed = transcript.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const payload: Record<string, string | null> = {
        transcript: trimmed,
        clientName: clientName.trim() || null,
        sessionDate: sessionDate || null,
      };

      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? "Failed to submit transcript.");
        return;
      }

      const { data } = await res.json();
      onSuccess({
        sessionId: data.sessionId ?? data.id,
        sessionShortId: data.sessionShortId ?? data.shortId,
        clientName: data.clientName ?? (clientName.trim() || null),
        sessionDate: data.sessionDate ?? (sessionDate || null),
      });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const formCard = (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "flex flex-col gap-[14px] rounded-2xl bg-card border border-sand-200 shadow-[0_10px_24px_rgba(40,3,15,0.08)] py-[18px] px-5",
        variant === "overlay" && "mx-auto max-w-[860px]",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[10px] min-w-0">
          <div className="flex items-center justify-center h-[28px] w-[28px] rounded-full bg-interactive-light shrink-0">
            <Plus className="h-[14px] w-[14px] text-interactive" strokeWidth={2.5} />
          </div>
          <h3 className="font-sans text-[15px] font-semibold leading-[18px] text-foreground shrink-0">
            {title}
          </h3>
          {subtitle && (
            <span className="text-xs text-sand-400 truncate">· {subtitle}</span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex items-center justify-center h-[26px] w-[26px] rounded-full bg-sand-100 text-sand-500 hover:text-foreground transition-colors shrink-0"
        >
          <X className="h-[14px] w-[14px]" />
        </button>
      </div>

      {/* Client + date — 2:1 ratio */}
      <div className="flex gap-3">
        <div className="flex flex-col gap-1 flex-[2] min-w-0">
          <Label htmlFor="transcript-form-client-name" className={LABEL_BASE}>
            Client name (optional)
          </Label>
          <Input
            id="transcript-form-client-name"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="e.g. Joanna"
            disabled={isSubmitting}
            className={FIELD_BASE}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <Label htmlFor="transcript-form-session-date" className={LABEL_BASE}>
            Session date{requireSessionDate ? "" : " (optional)"}
          </Label>
          <Input
            id="transcript-form-session-date"
            type="date"
            value={sessionDate}
            onChange={(e) => {
              setSessionDate(e.target.value);
              if (e.target.value) setDateInvalid(false);
            }}
            disabled={isSubmitting}
            className={cn(FIELD_BASE, dateInvalid && "ring-2 ring-error")}
          />
        </div>
      </div>

      {/* Transcript */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <Label htmlFor="transcript-form-transcript" className={LABEL_BASE}>
            Transcript
          </Label>
          <span className="text-[11px] text-sand-400">
            {transcript.length.toLocaleString()} characters
          </span>
        </div>
        <Textarea
          id="transcript-form-transcript"
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder="Paste the session transcript here..."
          disabled={isSubmitting}
          className="h-[140px] min-h-[140px] resize-none rounded-[10px] bg-sand-50 border-sand-200 py-3 px-[14px] font-mono text-[11px] md:text-[11px] text-sand-600 shadow-none"
        />
      </div>

      {error && <p className="text-sm text-error">{error}</p>}

      {/* Footer — no divider */}
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-sand-500">{helperText}</p>
        <div className="flex gap-[10px] shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="h-9 rounded-[10px] px-4 text-[13px] font-medium border-sand-200"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!transcript.trim() || isSubmitting}
            className="h-9 rounded-[10px] px-[18px] bg-interactive text-white hover:bg-interactive-hover text-[13px] font-medium shadow-sm"
          >
            {isSubmitting ? "Submitting..." : submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );

  if (variant === "overlay") {
    return (
      <>
        <div
          className="absolute inset-0 bg-background/60 z-10"
          onClick={onClose}
          aria-hidden="true"
        />
        <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-4">
          {formCard}
        </div>
      </>
    );
  }

  return <div className="mb-3">{formCard}</div>;
}
